import { Box, Typography } from '@mui/material';
import { UserEntity } from 'app/interfaces/entity';
import { PlayerTile } from 'modules/users/components/PlayerTile/PlayerTile';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDefaultUserMenuActions } from 'shared/hooks';
import { useStyles } from './SearchResult.styles';

interface SearchResultProps {
  activeUsers?: UserEntity[];
  users: UserEntity[];
  selectionLength?: number;
  onUserSelect?: (users: UserEntity[]) => void;
}

export const SearchResult: FC<SearchResultProps> = ({
  activeUsers = [],
  selectionLength,
  onUserSelect,
  users,
}) => {
  const { classes } = useStyles();
  const { privateMessageActions } = useDefaultUserMenuActions();

  const activeUsersMap = useMemo(
    () => new Map(activeUsers?.map((user) => [user.userId, user])),
    [activeUsers]
  );

  const [selectedUsers, setSelectedUsers] = useState<Map<string, UserEntity>>(activeUsersMap);
  const isSelectAvailable = useMemo(
    () => !selectionLength || selectionLength > selectedUsers.size,
    [selectedUsers, selectionLength]
  );

  const selectUser = useCallback(
    (user?: UserEntity) => {
      if (!user || !isSelectAvailable) {
        return;
      }

      setSelectedUsers((prevState) => {
        return new Map(prevState.set(user?.userId, user));
      });
    },
    [setSelectedUsers, isSelectAvailable]
  );

  const removeUser = useCallback(
    (user?: UserEntity) => {
      if (!user) {
        return;
      }

      setSelectedUsers((prevState) => {
        prevState.delete(user?.userId);
        onUserSelect?.(Array.from(prevState.values()));
        return new Map(prevState);
      });
    },
    [onUserSelect]
  );

  const actions = useMemo(
    () => [
      {
        label: 'Delete',
        handler: removeUser,
        isAllowedWithoutAuthorization: true,
      },
      ...privateMessageActions,
    ],
    [privateMessageActions, removeUser]
  );

  useEffect(() => {
    onUserSelect?.(Array.from(selectedUsers.values()));
  }, [onUserSelect, selectedUsers]);

  if (!users) {
    return null;
  }

  return (
    <Box>
      {Boolean(selectedUsers.size) && (
        <Box>
          <Typography className={classes.header}>{`Selected (${
            selectionLength ? `${selectedUsers.size}/${selectionLength}` : selectedUsers.size
          })`}</Typography>
          {Array.from(selectedUsers.values()).map((user) => (
            <Box key={user?.userId} className={classes.card}>
              <PlayerTile user={user} actions={actions} />
            </Box>
          ))}
        </Box>
      )}
      {Boolean(users?.length) && <Typography className={classes.header}>Search Results</Typography>}
      {users.map((user) => {
        if (selectedUsers.has(user.userId)) {
          return null;
        }

        return (
          <Box key={user?.userId} className={classes.card}>
            <PlayerTile user={user} onClick={selectUser} actions={privateMessageActions} />
          </Box>
        );
      })}
    </Box>
  );
};
